/**
 * Firebase Analytics wrapper.
 *
 * Initialisation is asynchronous and gated by `isSupported()` (the browser
 * must support IndexedDB + cookies; some privacy-hardened browsers don't).
 * If unsupported or disabled, every helper is a no-op so call sites never
 * need to branch.
 *
 * Events we care about:
 *   - `page_view`          — auto-logged on every route change
 *   - `login`              — auth state change (no user identity)
 *   - `trial_activated`    — self-heal subscription CTA
 *   - `booking_created`    — new Reserva saved to Firestore
 *   - `booking_cancelled`  — cancellation modal confirmed
 *   - `player_created`     — new CRM player saved
 *   - `series_created`     — new recurring series saved
 *   - `series_status_changed` — pause / resume / cancel / complete
 *   - `feedback_submitted` — internal feedback dialog submitted
 *
 * Use via `track('event_name', { ...params })` from anywhere — no direct
 * Firebase SDK imports outside this file.
 */

import {
  initializeAnalytics,
  isSupported,
  logEvent,
  setUserId,
  setUserProperties,
  type Analytics,
} from 'firebase/analytics'
import { app, env } from './firebase/client'
import { logger } from './logger'

let analytics: Analytics | null = null
let initPromise: Promise<Analytics | null> | null = null
/**
 * Lazily initialise Analytics. Called once during `main.tsx` bootstrap — the
 * returned promise resolves when the SDK is ready (or null when unsupported).
 * Events logged before init are silently dropped; that's fine for our use
 * since the first event (page_view) fires after React's router mounts.
 */
export function initAnalytics(): Promise<Analytics | null> {
  if (initPromise) return initPromise
  initPromise = (async () => {
    try {
      const supported = await isSupported()
      if (!supported) {
        logger.i('[analytics] not supported in this environment — skipping')
        return null
      }
      analytics = initializeAnalytics(app, { config: { send_page_view: false, page_location: window.location.origin + '/', page_referrer: '', allow_google_signals: false, allow_ad_personalization_signals: false } })
      setUserId(analytics, null)
      setUserProperties(analytics, { club_id: '' })
      logger.i(`[analytics] initialised (${env})`)
      return analytics
    } catch (err) {
      logger.e('[analytics] init failed', err)
      return null
    }
  })()
  return initPromise
}

/**
 * Fire a custom event. Safe to call before init — drops silently.
 * Parameter keys follow GA4 conventions: snake_case, ≤ 40 chars,
 * values must be string / number / boolean (no nested objects).
 *
 * Identity parameters are dropped; product metrics do not need entity IDs.
 */
export function track(
  name: string,
  params: Record<string, string | number | boolean | undefined> = {},
): void {
  if (!analytics) return
  const cleaned: Record<string, string | number | boolean> = {}
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || /(^|_)(id|uid|email|phone|name|token|code)($|_)/i.test(k)) continue
    cleaned[k] = v
  }
  try {
    logEvent(analytics, name, cleaned)
  } catch (err) {
    logger.w(`[analytics] event ${name} failed`, err)
  }
}

/** Clear legacy analytics identity; retain caller compatibility. */
export function setAnalyticsUser(_uid: string | null): void {
  if (!analytics) return
  try {
    setUserId(analytics, null)
  } catch (err) {
    logger.w('[analytics] setUserId failed', err)
  }
}

/** Clear the legacy club property; never attach club identity to metrics. */
export function setAnalyticsClub(_clubId: string | null): void {
  if (!analytics) return
  try {
    setUserProperties(analytics, { club_id: '' })
  } catch (err) {
    logger.w('[analytics] setUserProperties failed', err)
  }
}

/** Log an automatic `page_view` whenever the router commits a new route. */
export function trackPageView(path: string, title?: string): void {
  track('page_view', {
    page_path: path.split(/[?#]/)[0] ?? '/',
    ...(title ? { page_title: title } : {}),
  })
}

/** Signup events use the shared allowlist and never inherit event entity IDs. */
export { funnelError, createValidationReporter } from '../../../../packages/core/src/signup-funnel'
import { createFunnelTracker } from '../../../../packages/core/src/signup-funnel'
export const trackSignupFunnel = createFunnelTracker(async params => {
  const instance = analytics ?? await initAnalytics()
  if (!instance) return false
  logEvent(instance, 'club_funnel', params)
  const legacy = params.stage === 'dashboard_entered' ? 'club_signup_dashboard_entered' : params.stage === 'setup_step_confirmed' ? 'club_setup_confirmed' : null
  if (legacy) logEvent(instance, legacy, params)
  return true
}, { surface: 'app', environment: env })
